/**
 * StripeService.ts
 * Client-side Stripe integration for BASSline SaaS subscriptions.
 * 
 * Architecture:
 * - Uses Stripe Checkout (redirect) for new subscriptions.
 * - Uses Stripe Customer Portal for managing existing subscriptions.
 * - Firestore stores subscription state as cache (Stripe is source of truth via webhooks).
 * - All Stripe sessions are created via Firebase Cloud Functions (to protect secret key).
 * 
 * For DreamHost static hosting, only the Stripe.js public key is needed client-side.
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ─── Configuration ───────────────────────────────────────────────────
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
if (!STRIPE_PUBLISHABLE_KEY && import.meta.env.PROD) {
  console.error('[StripeService] VITE_STRIPE_PUBLISHABLE_KEY is not set. Stripe features are disabled.');
}
const TRIAL_PERIOD_DAYS = 90; // 3 months trial

// ─── Types ───────────────────────────────────────────────────────────
export interface SubscriptionInfo {
  status: 'active' | 'trialing' | 'grace' | 'past_due' | 'canceled' | 'inactive';
  subscribed: boolean;
  subscription_tier: string | null;
  subscription_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  trial_end: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface PricePlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  stripe_price_id: string;
  features: string[];
  popular?: boolean;
}

// ─── Default Plans (configurable via env or Firestore) ───────────────
export const DEFAULT_PLANS: PricePlan[] = [
  {
    id: 'pro_monthly',
    name: 'Pro',
    description: 'Todo lo que necesitas para gestionar tus eventos profesionalmente.',
    price: 29.99,
    currency: 'USD',
    interval: 'month',
    stripe_price_id: 'price_1TFAkmRS4GlmlM1DVJ99T8EU',
    features: [
      'Eventos ilimitados',
      'Cotizaciones públicas',
      'Gestión de inventario',
      'Reportes avanzados',
      'Soporte prioritario',
    ],
    popular: true,
  },
  {
    id: 'pro_yearly',
    name: 'Pro Anual',
    description: 'Ahorra 20% con el plan anual.',
    price: 287.90,
    currency: 'USD',
    interval: 'year',
    stripe_price_id: 'price_1TFAliRS4GlmlM1DFyDq2qFg',
    features: [
      'Todo en Pro mensual',
      '2 meses gratis',
      'Soporte prioritario',
    ],
  },
];

// ─── Stripe Service ──────────────────────────────────────────────────
class StripeService {
  private stripePromise: Promise<Stripe | null> | null = null;
  private functions = getFunctions();

  /**
   * Initialize Stripe.js (lazy loaded)
   */
  private getStripe(): Promise<Stripe | null> {
    if (!this.stripePromise) {
      if (!STRIPE_PUBLISHABLE_KEY) {
        console.warn('[StripeService] VITE_STRIPE_PUBLISHABLE_KEY not set. Stripe features disabled.');
        this.stripePromise = Promise.resolve(null);
      } else {
        this.stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
      }
    }
    return this.stripePromise;
  }

  /**
   * Check if Stripe is configured
   */
  isConfigured(): boolean {
    return !!STRIPE_PUBLISHABLE_KEY;
  }

  /**
   * Create a Stripe Checkout Session and redirect the user.
   * The session is created server-side via Firebase Cloud Function.
   */
  async createCheckoutSession(
    userId: string,
    userEmail: string,
    priceId: string,
  ): Promise<void> {
    const stripe = await this.getStripe();
    if (!stripe) {
      throw new Error('Stripe no está configurado. Contacte al administrador.');
    }

    try {
      const createCheckout = httpsCallable(this.functions, 'createStripeCheckoutSession');
      const result = await createCheckout({
        priceId,
        userId,
        userEmail,
        trialPeriodDays: TRIAL_PERIOD_DAYS,
        successUrl: `${window.location.origin}/settings?tab=subscription&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/settings?tab=subscription&canceled=true`,
      });

      const { sessionId, url } = result.data as { sessionId: string; url?: string };

      // Redirect to Stripe Checkout
      if (url) {
        window.location.href = url;
      } else {
        // Fallback: construct the checkout URL from session ID
        window.location.href = `https://checkout.stripe.com/pay/${sessionId}`;
      }
    } catch (error: unknown) {
      console.error('[StripeService] Checkout error:', error);
      throw error;
    }
  }

  /**
   * Redirect user to the Stripe Customer Portal for subscription management.
   * Portal session created server-side via Firebase Cloud Function.
   */
  async openCustomerPortal(userId: string): Promise<void> {
    try {
      const createPortal = httpsCallable(this.functions, 'createStripePortalSession');
      const result = await createPortal({
        userId,
        returnUrl: `${window.location.origin}/settings?tab=subscription`,
      });

      const { url } = result.data as { url: string };
      window.location.href = url;
    } catch (error: unknown) {
      console.error('[StripeService] Portal error:', error);
      throw error;
    }
  }

  /**
   * Get subscription data from Firestore (cached from Stripe webhooks).
   */
  async getSubscriptionData(userId: string): Promise<SubscriptionInfo | null> {
    try {
      const subDoc = await getDoc(doc(db, 'subscriptions', userId));
      if (subDoc.exists()) {
        return subDoc.data() as SubscriptionInfo;
      }
      return null;
    } catch (error) {
      console.error('[StripeService] Error fetching subscription:', error);
      return null;
    }
  }

  /**
   * Listen to real-time subscription changes (Firestore onSnapshot).
   */
  onSubscriptionChange(
    userId: string,
    callback: (data: SubscriptionInfo | null) => void,
  ): Unsubscribe {
    return onSnapshot(
      doc(db, 'subscriptions', userId),
      (snapshot) => {
        if (snapshot.exists()) {
          callback(snapshot.data() as SubscriptionInfo);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error('[StripeService] Subscription listener error:', error);
        callback(null);
      },
    );
  }

  /**
   * Initialize a default subscription document for new users (inactive).
   */
  async initializeSubscription(userId: string): Promise<void> {
    const existing = await this.getSubscriptionData(userId);
    if (!existing) {
      await setDoc(doc(db, 'subscriptions', userId), {
        status: 'inactive',
        subscribed: false,
        subscription_tier: null,
        subscription_end: null,
        stripe_customer_id: null,
        stripe_subscription_id: null,
        trial_end: null,
        current_period_end: null,
        cancel_at_period_end: false,
      } satisfies SubscriptionInfo);
    }
  }

  /**
   * Get available plans
   */
  getPlans(): PricePlan[] {
    return DEFAULT_PLANS;
  }

  /**
   * Get trial period in days
   */
  getTrialDays(): number {
    return TRIAL_PERIOD_DAYS;
  }
}

export default new StripeService();
